import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import API_ENDPOINTS from '../ApiEndPoints';
import WorkflowSettingsPanel from './WorkflowSettingsPanel';
import './WorkflowBoard.css';

const STAGE_PAGE_SIZE = 40;

const formatDescription = (value, limit = 160) => {
  if (!value) {
    return '';
  }
  const trimmed = value.trim();
  if (trimmed.length <= limit) {
    return trimmed;
  }
  return `${trimmed.slice(0, limit)}…`;
};

const copyToClipboard = async (text) => {
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const temp = document.createElement('textarea');
    temp.value = text;
    temp.style.position = 'fixed';
    temp.style.opacity = '0';
    document.body.appendChild(temp);
    temp.select();
    document.execCommand('copy');
    document.body.removeChild(temp);
    return true;
  } catch (error) {
    return false;
  }
};

const ItemCard = ({
  item,
  stage,
  showDescriptions,
  showTags,
  onRemove,
  removingItemId,
  dragHandleProps = {},
  draggableProps = {},
  innerRef = null,
  isDragging = false,
}) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dueBadge = useMemo(() => {
    if (typeof item.due_in_days !== 'number') {
      return null;
    }
    if (item.due_in_days < 0) {
      return (
        <span className="workflow-card-pill is-overdue">
          {Math.abs(item.due_in_days)} day{Math.abs(item.due_in_days) === 1 ? '' : 's'} overdue
        </span>
      );
    }
    if (item.due_in_days === 0) {
      return <span className="workflow-card-pill is-today">Due today</span>;
    }
    return (
      <span className="workflow-card-pill">
        Due in {item.due_in_days} day{item.due_in_days === 1 ? '' : 's'}
      </span>
    );
  }, [item.due_in_days]);

  const contactInitials = useMemo(() => {
    const first = item.contact?.first_name?.charAt(0) || '';
    const last = item.contact?.last_name?.charAt(0) || '';
    const initials = `${first}${last}`.trim();
    return initials || '#';
  }, [item.contact?.first_name, item.contact?.last_name]);

  const updatedAt = useMemo(() => (item.updated_at ? new Date(item.updated_at) : null), [item.updated_at]);
  const hasContactDetails = useMemo(
    () => Boolean(item.contact?.first_name || item.contact?.last_name || item.contact?.email),
    [item.contact?.first_name, item.contact?.last_name, item.contact?.email]
  );
  const contactName = useMemo(() => {
    if (item.contact?.first_name || item.contact?.last_name) {
      return `${item.contact.first_name || ''} ${item.contact.last_name || ''}`.trim();
    }
    return '';
  }, [item.contact?.first_name, item.contact?.last_name]);
  const isRemoving = removingItemId === item.id;

  return (
    <div
      className={`workflow-card-compact${isDragging ? ' is-dragging' : ''}`}
      ref={innerRef}
      {...draggableProps}
      {...dragHandleProps}
    >
      <div className="workflow-card-header">
        <div>
          <div className="workflow-card-title">{item.title || `Record #${item.id}`}</div>
          {item.contact?.company && (
            <div className="workflow-card-company">{item.contact.company}</div>
          )}
        </div>
        <div className="workflow-card-header-meta">
          {item.status && <span className="workflow-card-status">{item.status}</span>}
          {dueBadge}
        </div>
      </div>
      <div className="workflow-card-body">
        {hasContactDetails && (
          <div className="workflow-card-contact">
            <div className="workflow-card-avatar" aria-hidden="true">
              {contactInitials}
            </div>
            <div>
              {contactName && <div className="workflow-card-name">{contactName}</div>}
              {item.contact?.email && (
                <a className="workflow-card-email" href={`mailto:${item.contact.email}`}>
                  {item.contact.email}
                </a>
              )}
            </div>
          </div>
        )}
        {showDescriptions && item.description && (
          <p className="workflow-card-description">
            {formatDescription(item.description, 120)}
          </p>
        )}
        {showTags && item.tags_html && (
          <div
            className="workflow-card-tags"
            dangerouslySetInnerHTML={{ __html: item.tags_html }}
          />
        )}
      </div>
      <div className="workflow-card-footer">
        <div className="workflow-card-links">
          {item.links?.view && (
            <a className="workflow-card-icon" href={item.links.view} target="_blank" rel="noreferrer">
              <i className="fa fa-eye" aria-hidden="true" />
              <span className="sr-only">View</span>
            </a>
          )}
          {item.links?.edit && (
            <a className="workflow-card-icon" href={item.links.edit} target="_blank" rel="noreferrer">
              <i className="fa fa-pen" aria-hidden="true" />
              <span className="sr-only">Edit</span>
            </a>
          )}
        </div>
        <div className="workflow-card-actions">
          {!confirmDelete && (
            <button
              type="button"
              className="workflow-card-icon is-danger"
              disabled={isRemoving}
              onClick={() => setConfirmDelete(true)}
            >
              <i className="fa fa-trash" aria-hidden="true" />
              <span className="sr-only">Remove from workflow</span>
            </button>
          )}
          {confirmDelete && (
            <div className="workflow-card-confirm">
              <button
                type="button"
                className="workflow-card-icon is-success"
                disabled={isRemoving}
                onClick={() => {
                  setConfirmDelete(false);
                  onRemove(item, stage);
                }}
              >
                <i className="fa fa-check" aria-hidden="true" />
                <span className="sr-only">Confirm remove</span>
              </button>
              <button
                type="button"
                className="workflow-card-icon is-muted"
                onClick={() => setConfirmDelete(false)}
              >
                <i className="fa fa-times" aria-hidden="true" />
                <span className="sr-only">Cancel remove</span>
              </button>
            </div>
          )}
        </div>
      </div>
      {updatedAt && (
        <div className="workflow-card-updated">
          Updated {updatedAt.toLocaleDateString()} {updatedAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
        </div>
      )}
    </div>
  );
};

const StageColumn = ({
  stage,
  items,
  meta,
  loading,
  error,
  showDescriptions,
  showTags,
  onRemoveItem,
  removingItemId,
  onLoadMore,
  onRefresh,
  onHide,
}) => (
  <div className="workflow-stage-column">
    <div className="workflow-stage-header">
      <div>
        <div className="workflow-stage-title">{stage.title}</div>
        <div className="workflow-stage-count">{stage.count} open</div>
      </div>
      <div className="workflow-stage-controls">
        <button type="button" className="btn btn-link btn-xs" onClick={() => onRefresh(stage)} disabled={loading}>
          Refresh
        </button>
        <button type="button" className="btn btn-link btn-xs" onClick={() => onHide(stage)}>
          Hide
        </button>
      </div>
    </div>
    {stage.description && (
      <p className="workflow-stage-description">{stage.description}</p>
    )}
    <Droppable droppableId={stage.tid}>
      {(provided, snapshot) => (
        <div
          className={`workflow-stage-body${snapshot.isDraggingOver ? ' is-hovered' : ''}`}
          ref={provided.innerRef}
          {...provided.droppableProps}
        >
          {error && <div className="alert alert-danger">{error}</div>}
          {items?.length > 0 && items.map((item, index) => (
            <Draggable key={item.id} draggableId={String(item.id)} index={index}>
              {(draggableProvided, draggableSnapshot) => (
                <ItemCard
                  item={item}
                  stage={stage}
                  showDescriptions={showDescriptions}
                  showTags={showTags}
                  onRemove={onRemoveItem}
                  removingItemId={removingItemId}
                  dragHandleProps={draggableProvided.dragHandleProps}
                  draggableProps={draggableProvided.draggableProps}
                  innerRef={draggableProvided.innerRef}
                  isDragging={draggableSnapshot.isDragging}
                />
              )}
            </Draggable>
          ))}
          {!loading && (!items || items.length === 0) && (
            <p className="workflow-stage-empty">No records in this stage.</p>
          )}
          {loading && <p className="workflow-stage-empty">Loading…</p>}
          {provided.placeholder}
        </div>
      )}
    </Droppable>
    {meta && meta.current_page < meta.last_page && (
      <div className="workflow-stage-footer">
        <button type="button" className="btn btn-link btn-xs" onClick={() => onLoadMore(stage)} disabled={loading}>
          Load more
        </button>
      </div>
    )}
  </div>
);

const WorkflowBoard = ({ workflowId }) => {
  const [board, setBoard] = useState(null);
  const [loadingBoard, setLoadingBoard] = useState(false);
  const [error, setError] = useState(null);
  const [infoMessage, setInfoMessage] = useState(null);
  const [selectedFilterTid, setSelectedFilterTid] = useState(null);
  const [persistingFilter, setPersistingFilter] = useState(false);
  const [preferences, setPreferences] = useState({ show_descriptions: false, show_tags: false });
  const [searchInput, setSearchInput] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [stageItems, setStageItems] = useState({});
  const [stageMeta, setStageMeta] = useState({});
  const [stageErrors, setStageErrors] = useState({});
  const [stageLoading, setStageLoading] = useState({});
  const [loadKey, setLoadKey] = useState(0);
  const [savingPrefs, setSavingPrefs] = useState(false);
  const [adopting, setAdopting] = useState(false);
  const [removingItemId, setRemovingItemId] = useState(null);
  const [boardStageLoadSignal, setBoardStageLoadSignal] = useState(0);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const stageMetaRef = useRef(stageMeta);
  useEffect(() => {
    stageMetaRef.current = stageMeta;
  }, [stageMeta]);

  const boardRef = useRef(board);
  useEffect(() => {
    boardRef.current = board;
  }, [board]);

  const requestReload = useCallback(() => {
    setLoadKey((value) => value + 1);
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadBoard = async () => {
      if (!workflowId) {
        return;
      }
      setLoadingBoard(true);
      setError(null);
      try {
        const params = {};
        if (selectedFilterTid) {
          params.filter = selectedFilterTid;
        }
        if (appliedSearch) {
          params.search = appliedSearch;
        }
        const { data } = await axios.get(API_ENDPOINTS.workflowBoard.board(workflowId), { params });
        if (cancelled) {
          return;
        }
  const boardPayload = { ...data };
        setBoard(boardPayload);
        setPreferences({
          show_descriptions: !!boardPayload.preferences?.show_descriptions,
          show_tags: !!boardPayload.preferences?.show_tags,
        });
        setSelectedFilterTid(boardPayload.preferences?.selected_filter_tid ?? null);
        setAppliedSearch(boardPayload.preferences?.search ?? '');
        setSearchInput(boardPayload.preferences?.search ?? '');
        setStageItems({});
        setStageMeta({});
        setStageErrors({});
        setStageLoading({});
        setBoardStageLoadSignal((value) => value + 1);
      } catch (apiError) {
        if (!cancelled) {
          setError(apiError?.response?.data?.message || 'Unable to load workflow board.');
        }
      } finally {
        if (!cancelled) {
          setLoadingBoard(false);
        }
      }
    };

    loadBoard();
    return () => {
      cancelled = true;
    };
  }, [workflowId, loadKey]);

  useEffect(() => {
    const timer = setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== appliedSearch) {
        setAppliedSearch(trimmed);
        requestReload();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput, appliedSearch]);

  const visibleStages = useMemo(() => {
    if (!board?.stages) {
      return [];
    }
    return board.stages.filter((stage) => !stage.hidden);
  }, [board]);

  const hiddenStages = useMemo(() => {
    if (!board?.stages) {
      return [];
    }
    return board.stages.filter((stage) => stage.hidden);
  }, [board]);

  const stageByTid = useMemo(() => {
    if (!board?.stages) {
      return {};
    }
    return board.stages.reduce((map, stage) => {
      map[stage.tid] = stage;
      return map;
    }, {});
  }, [board]);

  const shareUrl = useMemo(() => {
    if (!board?.filters?.share_base_url) {
      return '';
    }
    if (selectedFilterTid) {
      return `${board.filters.share_base_url}?filter=${encodeURIComponent(selectedFilterTid)}`;
    }
    return board.filters.share_base_url;
  }, [board, selectedFilterTid]);

  const loadStageItems = useCallback(async (stage, { reset = false } = {}) => {
    if (!stage || !workflowId) {
      return;
    }
    setStageErrors((prev) => ({ ...prev, [stage.id]: null }));

    if (!reset) {
      const meta = stageMetaRef.current[stage.id];
      if (meta && meta.current_page >= meta.last_page) {
        return;
      }
    }

    const nextPage = reset ? 1 : (stageMetaRef.current[stage.id]?.current_page || 0) + 1;

    setStageLoading((prev) => ({ ...prev, [stage.id]: true }));
    try {
      const params = { per_page: STAGE_PAGE_SIZE, page: nextPage };
      if (selectedFilterTid) {
        params.filter = selectedFilterTid;
      }
      if (appliedSearch) {
        params.search = appliedSearch;
      }
      const { data } = await axios.get(
        API_ENDPOINTS.workflowBoard.stageItems(workflowId, stage.id),
        { params }
      );
      setStageItems((prev) => ({
        ...prev,
        [stage.id]: reset ? data.data : [ ...(prev[stage.id] || []), ...(data.data || []) ],
      }));
      const meta = data.meta || {
        current_page: nextPage,
        last_page: nextPage,
        total: data.data?.length || 0,
        per_page: STAGE_PAGE_SIZE,
      };
      setStageMeta((prev) => ({
        ...prev,
        [stage.id]: meta,
      }));
    } catch (apiError) {
      setStageErrors((prev) => ({
        ...prev,
        [stage.id]: apiError?.response?.data?.message || 'Unable to load items for this stage.',
      }));
    } finally {
      setStageLoading((prev) => ({ ...prev, [stage.id]: false }));
    }
  }, [workflowId, selectedFilterTid, appliedSearch]);

  useEffect(() => {
    if (!boardRef.current?.stages) {
      return;
    }
    boardRef.current.stages.filter((stage) => !stage.hidden).forEach((stage) => {
      loadStageItems(stage, { reset: true });
    });
  }, [boardStageLoadSignal, loadStageItems]);

  const persistPreferences = async (payload) => {
    if (!workflowId) {
      return;
    }
    setSavingPrefs(true);
    try {
      const { data } = await axios.post(
        API_ENDPOINTS.workflowBoard.preferences(workflowId),
        payload
      );
      if (data?.preferences) {
        setPreferences({
          show_descriptions: !!data.preferences.show_descriptions,
          show_tags: !!data.preferences.show_tags,
        });
      }
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to update preferences.');
      throw apiError;
    } finally {
      setSavingPrefs(false);
    }
  };

  const handlePreferenceToggle = async (key) => {
    const next = !preferences[key];
    setPreferences((prev) => ({ ...prev, [key]: next }));
    try {
      await persistPreferences({ [key]: next });
    } catch (error) {
      setPreferences((prev) => ({ ...prev, [key]: !next }));
    }
  };

  const handleHideStage = async (stage, hide = true) => {
    if (!board) {
      return;
    }
    const previousStages = board.stages;
    const updatedStages = previousStages.map((candidate) =>
      candidate.tid === stage.tid ? { ...candidate, hidden: hide } : candidate
    );
    setBoard((prev) => (prev ? { ...prev, stages: updatedStages } : prev));
    const hiddenStageTids = updatedStages.filter((candidate) => candidate.hidden).map((candidate) => candidate.tid);
    try {
      await persistPreferences({ hidden_stage_tids: hiddenStageTids });
      if (!hide) {
        loadStageItems(stage, { reset: true });
      }
    } catch (error) {
      setBoard((prev) => (prev ? { ...prev, stages: previousStages } : prev));
    }
  };

  const handleFilterChange = async (event) => {
    const tid = event.target.value || null;
    try {
      await persistFilterSelection(tid);
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to update filter.');
    }
  };

  const handleClearFilter = async () => {
    try {
      await persistFilterSelection(null);
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to clear filters.');
    }
  };

  const persistFilterSelection = useCallback(async (tid, { showToast = true } = {}) => {
    if (!workflowId) {
      return;
    }
    setPersistingFilter(true);
    try {
      await axios.post(API_ENDPOINTS.workflowBoard.filters(workflowId), { filter_tid: tid });
      setSelectedFilterTid(tid);
      if (showToast) {
        setInfoMessage(tid ? 'Filter applied.' : 'Filters cleared.');
      }
      requestReload();
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to update filter.');
      throw apiError;
    } finally {
      setPersistingFilter(false);
    }
  }, [workflowId, requestReload]);

  const handleAdoptOrphans = async () => {
    setAdopting(true);
    setInfoMessage(null);
    try {
      const { data } = await axios.post(
        API_ENDPOINTS.workflowBoard.adoptOrphans(workflowId)
      );
      setInfoMessage(`${data.updated} record${data.updated === 1 ? '' : 's'} adopted into stage ${data.stage_tid}.`);
      requestReload();
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to adopt orphaned records.');
    } finally {
      setAdopting(false);
    }
  };

  const handleMoveItem = useCallback(async (item, targetStageTid, stage) => {
    if (!targetStageTid || targetStageTid === stage.tid) {
      return;
    }
    const targetStage = stageByTid[targetStageTid];
    if (!targetStage) {
      return;
    }
    try {
      await axios.post(
        API_ENDPOINTS.workflowBoard.moveItem(workflowId, item.id),
        { stage_tid: targetStageTid }
      );
      setStageItems((prev) => {
        const updatedItem = {
          ...item,
          stage_tid: targetStage.tid,
          status: targetStageTid,
        };
        const sourceItems = (prev[stage.id] || []).filter((entry) => entry.id !== item.id);
        const destinationBase = (prev[targetStage.id] || []).filter((entry) => entry.id !== item.id);
        const destinationItems = [updatedItem, ...destinationBase];
        return {
          ...prev,
          [stage.id]: sourceItems,
          [targetStage.id]: destinationItems,
        };
      });
      setStageMeta((prev) => {
        const next = { ...prev };
        if (next[stage.id]) {
          next[stage.id] = {
            ...next[stage.id],
            total: Math.max(0, (next[stage.id].total || 1) - 1),
          };
        }
        if (next[targetStage.id]) {
          next[targetStage.id] = {
            ...next[targetStage.id],
            total: (next[targetStage.id].total || 0) + 1,
          };
        }
        return next;
      });
      setBoard((prev) => {
        if (!prev?.stages) {
          return prev;
        }
        return {
          ...prev,
          stages: prev.stages.map((candidate) => {
            if (candidate.id === stage.id) {
              return {
                ...candidate,
                count: Math.max(0, (candidate.count ?? 0) - 1),
              };
            }
            if (candidate.id === targetStage.id) {
              return {
                ...candidate,
                count: (candidate.count ?? 0) + 1,
              };
            }
            return candidate;
          }),
        };
      });
      setInfoMessage('Record moved successfully.');
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to move record.');
    }
  }, [stageByTid, workflowId]);

  const handleDragEnd = useCallback((result) => {
    const { source, destination, draggableId } = result || {};
    if (!destination || !source || source.droppableId === destination.droppableId) {
      return;
    }
    const sourceStage = stageByTid[source.droppableId];
    const targetStage = stageByTid[destination.droppableId];
    if (!sourceStage || !targetStage) {
      return;
    }
    const sourceItems = stageItems[sourceStage.id] || [];
    const draggedItem = sourceItems.find((entry) => String(entry.id) === draggableId);
    if (!draggedItem) {
      return;
    }
    handleMoveItem(draggedItem, targetStage.tid, sourceStage);
  }, [handleMoveItem, stageByTid, stageItems]);

  const handleRemoveItem = async (item, stage) => {
    setRemovingItemId(item.id);
    try {
      await axios.delete(API_ENDPOINTS.workflowBoard.removeItem(workflowId, item.id));
      setStageItems((prev) => ({
        ...prev,
        [stage.id]: (prev[stage.id] || []).filter((entry) => entry.id !== item.id),
      }));
      setInfoMessage('Record removed from workflow.');
      loadStageItems(stage, { reset: true });
      requestReload();
    } catch (apiError) {
      setError(apiError?.response?.data?.message || 'Unable to remove record.');
    } finally {
      setRemovingItemId(null);
    }
  };

  if (!workflowId) {
    return <div className="alert alert-warning">Workflow ID is required to load the board.</div>;
  }

  return (
    <div>
      {infoMessage && (
        <div className="workflow-board-toast is-success">
          <div className="workflow-board-toast-content">
            {infoMessage}
          </div>
          <button type="button" className="workflow-board-toast-close" onClick={() => setInfoMessage(null)}>
            &times;
          </button>
        </div>
      )}
      {error && (
        <div className="alert alert-danger alert-dismissable">
          <button type="button" className="close" onClick={() => setError(null)}>
            &times;
          </button>
          {error}
        </div>
      )}

      {loadingBoard && !board && <div className="alert alert-info">Loading workflow board…</div>}

      {board && (
        <>
          <div className="panel panel-default">
            <div className="clearfix panel-heading">
              <div className="pull-left">
                <h3 className="panel-title" style={{ marginBottom: 4 }}>{board.workflow?.title}</h3>
                {board.workflow?.description && (
                  <div
                    className="text-muted"
                    dangerouslySetInnerHTML={{ __html: board.workflow.description }}
                  />
                )}
                {board.workflow?.add_entry_url && (
                  <div className="btn-group btn-group-xs" style={{ marginTop: 8 }}>
                    <a className="btn btn-primary btn-xs" href={board.workflow.add_entry_url}>
                      <i className="ti-plus" style={{ fontSize: '11px', marginRight: 4 }} aria-hidden="true" />
                      Add new
                    </a>
                  </div>
                )}
              </div>
              <div className="pull-right btn-group btn-group-xs">
                <button
                  type="button"
                  className="btn btn-default btn-xs"
                  onClick={() => setSettingsOpen(true)}
                  title="Workflow settings"
                >
                  <i className="ti-settings" style={{ fontSize: '12px' }} aria-hidden="true" />
                  <span className="sr-only">Settings</span>
                </button>
              </div>
            </div>
          <div className="panel-body">
            <div className="workflow-board-toolbar">
              <div className="workflow-board-toolbar-group">
                <label className="workflow-board-label">Filters</label>
                <div className="workflow-board-field">
                  <select
                    className="form-control input-sm"
                    value={selectedFilterTid || ''}
                    onChange={handleFilterChange}
                    disabled={persistingFilter}
                  >
                    <option value="">All records</option>
                    {(board.filters?.available || []).map((filter) => (
                      <option key={filter.tid} value={filter.tid}>
                        {filter.title}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-link btn-xs"
                    disabled={persistingFilter}
                    onClick={handleClearFilter}
                  >
                    Clear
                  </button>
                </div>
              </div>
              <div className="workflow-board-toolbar-group">
                <label className="workflow-board-label">Search</label>
                <input
                  type="text"
                  className="form-control input-sm"
                  placeholder="Search title, description, tags"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      setAppliedSearch(event.target.value.trim());
                      requestReload();
                    }
                  }}
                />
              </div>
              <div className="workflow-board-toolbar-group">
                <label className="workflow-board-label">Preferences</label>
                <div className="btn-group btn-group-xs" role="group">
                  <button
                    type="button"
                    className={`btn ${preferences.show_descriptions ? 'btn-primary' : 'btn-default'}`}
                    onClick={() => handlePreferenceToggle('show_descriptions')}
                    disabled={savingPrefs}
                  >
                    Desc
                  </button>
                  <button
                    type="button"
                    className={`btn ${preferences.show_tags ? 'btn-primary' : 'btn-default'}`}
                    onClick={() => handlePreferenceToggle('show_tags')}
                    disabled={savingPrefs}
                  >
                    Tags
                  </button>
                </div>
              </div>
            </div>

            {board.filters?.current && (
              <div className="alert alert-info">
                <strong>Active filter:</strong> {board.filters.current.title}
                {Array.isArray(board.filters.current.conditions) && board.filters.current.conditions.length > 0 && (
                  <ul style={{ marginBottom: 0 }}>
                    {board.filters.current.conditions.map((condition, index) => (
                      <li key={index}>
                        <strong>{condition.field}</strong> {condition.operator || condition.condition || '='} <strong>{condition.value}</strong>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            {board.orphan_count > 0 && (
              <div className="clearfix alert alert-warning">
                <div className="pull-left">
                  {board.orphan_count} record{board.orphan_count === 1 ? ' is' : 's are'} not assigned to any stage.
                </div>
                <div className="pull-right">
                  <button
                    type="button"
                    className="btn btn-warning btn-xs"
                    onClick={handleAdoptOrphans}
                    disabled={adopting}
                  >
                    {adopting ? 'Adopting…' : 'Adopt orphans'}
                  </button>
                </div>
              </div>
            )}

            {board.warnings?.map((warning, index) => (
              <div className="alert alert-warning" key={index}>{warning}</div>
            ))}

            {hiddenStages.length > 0 && (
              <div className="alert alert-info">
                Hidden stages:
                {hiddenStages.map((stage) => (
                  <button
                    type="button"
                    key={stage.tid}
                    className="btn btn-link btn-xs"
                    onClick={() => handleHideStage(stage, false)}
                  >
                    {stage.title}
                  </button>
                ))}
              </div>
            )}

            <div className="workflow-stage-wrapper">
              <DragDropContext onDragEnd={handleDragEnd}>
                <div className="workflow-stage-row">
                  {visibleStages.length === 0 && (
                    <div className="alert alert-info workflow-stage-empty-board">
                      No stages are currently visible. Enable a stage from the hidden list or workflow settings.
                    </div>
                  )}
                  {visibleStages.map((stage) => (
                    <StageColumn
                      key={stage.id}
                      stage={stage}
                      items={stageItems[stage.id]}
                      meta={stageMeta[stage.id]}
                      loading={!!stageLoading[stage.id]}
                      error={stageErrors[stage.id]}
                      showDescriptions={preferences.show_descriptions}
                      showTags={preferences.show_tags}
                      onRemoveItem={handleRemoveItem}
                      removingItemId={removingItemId}
                      onLoadMore={(currentStage) => loadStageItems(currentStage, { reset: false })}
                      onRefresh={(currentStage) => loadStageItems(currentStage, { reset: true })}
                      onHide={(currentStage) => handleHideStage(currentStage, true)}
                    />
                  ))}
                </div>
              </DragDropContext>
            </div>
          </div>
          </div>
          <WorkflowSettingsPanel
            open={settingsOpen}
            onClose={() => setSettingsOpen(false)}
            workflowId={workflowId}
            workflowTitle={board.workflow?.title}
            stages={board.stages}
            selectedFilterTid={selectedFilterTid}
            appliedSearch={appliedSearch}
            onToggleStage={(stage, hide) => handleHideStage(stage, hide)}
            onAdoptOrphans={handleAdoptOrphans}
            adopting={adopting}
            onInfo={setInfoMessage}
            onError={setError}
            onRefreshBoard={requestReload}
            onPersistFilterSelection={persistFilterSelection}
            persistingFilter={persistingFilter}
          />
        </>
      )}
    </div>
  );
};

export default WorkflowBoard;
